import React, { useEffect, useRef, useState } from "react";

import {
  HeartPulse,
  ShoppingBag,
  GraduationCap,
  Landmark,
  Building2,
  Truck,
  Factory,
  Rocket,
  ArrowUpRight,
  CheckCircle2,
  Layers3,
  BrainCircuit,
  BarChart3,
  ShieldCheck,
  Workflow,
  Globe2,
} from "lucide-react";

import { Link } from "react-router-dom";

import "./Industries.css";


function Industries() {

  /*
  =========================================================
  INDUSTRIES DATA
  =========================================================
  */

  const industries = [
    {
      number: "01",
      icon: <HeartPulse />,
      title: "Healthcare",
      subtitle: "Digital healthcare experiences",
      text:
        "We build digital healthcare solutions that simplify patient experiences, streamline operations and make healthcare information easier to manage.",
      solutions: [
        "Healthcare websites",
        "Patient management platforms",
        "Appointment systems",
        "Health tracking applications",
        "Healthcare dashboards",
      ],
    },

    {
      number: "02",
      icon: <ShoppingBag />,
      title: "E-Commerce",
      subtitle: "Commerce built for growth",
      text:
        "We create modern e-commerce experiences that help businesses showcase products, manage customers and deliver smooth online shopping journeys.",
      solutions: [
        "E-commerce websites",
        "Product management",
        "Order management",
        "Customer dashboards",
        "Business analytics",
      ],
    },

    {
      number: "03",
      icon: <GraduationCap />,
      title: "Education",
      subtitle: "Technology for modern learning",
      text:
        "We develop education platforms that connect students, educators and organizations through accessible and engaging digital experiences.",
      solutions: [
        "Learning platforms",
        "Student portals",
        "Course management",
        "Assessment systems",
        "Education dashboards",
      ],
    },

    {
      number: "04",
      icon: <Landmark />,
      title: "Finance",
      subtitle: "Smarter financial technology",
      text:
        "We design secure digital systems for financial businesses, helping them automate workflows, organize information and deliver better customer experiences.",
      solutions: [
        "Financial dashboards",
        "Loan processing systems",
        "Customer portals",
        "Workflow automation",
        "Business analytics",
      ],
    },

    {
      number: "05",
      icon: <Building2 />,
      title: "Real Estate",
      subtitle: "Digital property experiences",
      text:
        "We create digital platforms that help real estate businesses manage properties, generate enquiries and connect buyers with the right opportunities.",
      solutions: [
        "Property websites",
        "Property listing platforms",
        "Lead management",
        "Agent dashboards",
        "Enquiry systems",
      ],
    },

    {
      number: "06",
      icon: <Truck />,
      title: "Logistics",
      subtitle: "Connected operations",
      text:
        "We build technology that improves visibility across logistics operations, helping businesses manage information, workflows and operational processes.",
      solutions: [
        "Logistics dashboards",
        "Shipment management",
        "Tracking systems",
        "Operations dashboards",
        "Workflow automation",
      ],
    },

    {
      number: "07",
      icon: <Factory />,
      title: "Manufacturing",
      subtitle: "Technology for better operations",
      text:
        "We develop business systems that help manufacturing companies organize processes, manage information and improve operational visibility.",
      solutions: [
        "Production dashboards",
        "Inventory systems",
        "Employee management",
        "Process automation",
        "Business reporting",
      ],
    },

    {
      number: "08",
      icon: <Rocket />,
      title: "Startups",
      subtitle: "From idea to scalable product",
      text:
        "We help startups turn ideas into practical digital products with focused development, modern technology and scalable architecture.",
      solutions: [
        "MVP development",
        "Web applications",
        "Mobile applications",
        "AI-powered products",
        "Automation systems",
      ],
    },
  ];


  /*
  =========================================================
  CAPABILITIES DATA
  =========================================================
  */

  const capabilities = [
    {
      icon: <Globe2 />,
      title: "Digital Experiences",
      text:
        "Modern websites and digital platforms designed around your customers and business objectives.",
    },

    {
      icon: <Layers3 />,
      title: "Business Applications",
      text:
        "Custom software systems that organize operations, information and workflows in one place.",
    },

    {
      icon: <BrainCircuit />,
      title: "AI & Machine Learning",
      text:
        "Practical AI solutions that turn business data into useful insights, predictions and intelligent experiences.",
    },

    {
      icon: <Workflow />,
      title: "Intelligent Automation",
      text:
        "Automated workflows that connect systems, reduce repetitive work and improve operational efficiency.",
    },

    {
      icon: <BarChart3 />,
      title: "Data & Analytics",
      text:
        "Dashboards and reporting systems that make important business information easier to understand.",
    },

    {
      icon: <ShieldCheck />,
      title: "Secure Systems",
      text:
        "Reliable application architecture with security, scalability and long-term maintainability in mind.",
    },
  ];


  /*
  =========================================================
  SECTION REFS
  =========================================================
  */

  const introRef = useRef(null);
  const solutionsRef = useRef(null);
  const capabilitiesRef = useRef(null);
  const whyRef = useRef(null);
  const ctaRef = useRef(null);


  /*
  =========================================================
  SECTION STATES
  =========================================================
  */

  const [visibleSections, setVisibleSections] = useState({
    intro: false,
    solutions: false,
    capabilities: false,
    why: false,
    cta: false,
  });


  /*
  =========================================================
  SCROLL OBSERVER
  =========================================================
  */

  useEffect(() => {

    const sections = [
      {
        ref: introRef,
        key: "intro",
      },

      {
        ref: solutionsRef,
        key: "solutions",
      },

      {
        ref: capabilitiesRef,
        key: "capabilities",
      },

      {
        ref: whyRef,
        key: "why",
      },

      {
        ref: ctaRef,
        key: "cta",
      },
    ];


    const observer = new IntersectionObserver(
      (entries) => {

        entries.forEach((entry) => {

          if (entry.isIntersecting) {

            const sectionKey =
              entry.target.dataset.animationSection;


            setVisibleSections((previous) => ({
              ...previous,
              [sectionKey]: true,
            }));


            observer.unobserve(
              entry.target
            );
          }

        });

      },
      {
        threshold: 0.1,
      }
    );


    sections.forEach(
      ({ ref, key }) => {

        if (ref.current) {

          ref.current.dataset.animationSection =
            key;

          observer.observe(
            ref.current
          );
        }

      }
    );


    return () => {
      observer.disconnect();
    };

  }, []);


  return (

    <div className="industries-page">


      {/* =====================================================
          HERO
      ===================================================== */}

      <section className="industries-hero">

        <div className="industries-hero-grid"></div>

        <div className="industries-hero-glow industries-glow-one"></div>

        <div className="industries-hero-glow industries-glow-two"></div>


        <div className="industries-container">


          {/* HERO CONTENT */}

          <div className="industries-hero-content">

            <div className="industries-label">

              <span></span>

              INDUSTRIES WE SERVE

            </div>


            <h1>
              Technology built
              <br />
              <span>
                around your industry.
              </span>
            </h1>


            <p>
              Every business has different customers, workflows and
              challenges. Nexis Fold creates digital products,
              software systems, AI solutions and automation designed
              around the way your industry actually works.
            </p>


            <div className="industries-hero-actions">

              <Link
                to="/contact"
                className="industries-primary-btn"
              >
                Discuss Your Business

                <ArrowUpRight size={17} />

              </Link>


              <a
                href="#industry-solutions"
                className="industries-secondary-btn"
              >
                Explore Industries

                <ArrowUpRight size={17} />

              </a>

            </div>

          </div>


          {/* HERO VISUAL */}

          <div className="industries-hero-visual">

            <div className="industry-visual-grid"></div>

            <div className="industry-visual-glow"></div>


            <div className="industry-visual-corner visual-corner-tl"></div>

            <div className="industry-visual-corner visual-corner-tr"></div>

            <div className="industry-visual-corner visual-corner-bl"></div>

            <div className="industry-visual-corner visual-corner-br"></div>


            <div className="industry-visual-header">

              <span>
                NEXIS FOLD
              </span>

              <span>
                INDUSTRIES
              </span>

            </div>


            <div className="industry-visual-main">

              <div className="industry-visual-ring industry-ring-one"></div>

              <div className="industry-visual-ring industry-ring-two"></div>


              <div className="industry-visual-icon">

                <BrainCircuit
                  size={48}
                  strokeWidth={1.4}
                />

              </div>


              <h3>
                Digital
                <br />
                Solutions
              </h3>


              <p>
                Technology built around
                real business requirements.
              </p>

            </div>


            <div className="industry-visual-footer">

              <span>
                01
              </span>

              <div></div>

              <span>
                08 INDUSTRIES
              </span>

            </div>

          </div>

        </div>

      </section>


      {/* =====================================================
          INTRO
      ===================================================== */}

      <section
        ref={introRef}
        className={`industry-intro ${
          visibleSections.intro
            ? "industry-section-visible"
            : ""
        }`}
      >

        <div className="industry-section-grid"></div>

        <div className="industries-container">

          <div className="industry-intro-grid">


            <div className="industry-reveal-left">

              <span className="industry-section-label">
                ONE TECHNOLOGY PARTNER
              </span>


              <h2>
                Different industries.
                <br />
                <span>
                  One technology partner.
                </span>
              </h2>

            </div>


            <div className="industry-intro-copy">

              <p>
                From customer-facing websites to internal business
                systems, we help organizations use technology to
                solve practical problems and create measurable
                improvements.
              </p>


              <p>
                Our approach combines product thinking, software
                engineering, artificial intelligence and automation
                so that the technology fits your business instead
                of forcing your business to adapt to technology.
              </p>

            </div>

          </div>

        </div>

      </section>


      {/* =====================================================
          INDUSTRY SOLUTIONS
      ===================================================== */}

      <section
        ref={solutionsRef}
        className={`industry-solutions ${
          visibleSections.solutions
            ? "industry-section-visible"
            : ""
        }`}
        id="industry-solutions"
      >

        <div className="industry-section-grid"></div>

        <div className="industries-container">


          <div className="industry-heading-row">

            <div className="industry-reveal-left">

              <span className="industry-section-label">
                INDUSTRY SOLUTIONS
              </span>


              <h2>
                Built around
                <br />
                <span>
                  real business needs.
                </span>
              </h2>

            </div>


            <p className="industry-reveal-right">
              Explore how Nexis Fold can design and develop
              technology solutions for different industries,
              business models and operational requirements.
            </p>

          </div>


          <div className="industry-solutions-grid">

            {industries.map(
              (industry, index) => (

                <article
                  className={`industry-solution-card industry-card-${(
                    index % 4
                  ) + 1}`}
                  key={industry.number}
                >

                  <div className="industry-card-grid"></div>

                  <div className="industry-card-glow"></div>


                  <div className="industry-card-top">

                    <div className="industry-card-icon">

                      {industry.icon}

                    </div>


                    <span className="industry-card-number">

                      {industry.number}

                    </span>

                  </div>


                  <div className="industry-card-content">

                    <span className="industry-card-subtitle">

                      {industry.subtitle}

                    </span>


                    <h3>
                      {industry.title}
                    </h3>


                    <p>
                      {industry.text}
                    </p>


                    <div className="industry-solutions-list">

                      {industry.solutions.map(
                        (
                          solution,
                          solutionIndex
                        ) => (

                          <div
                            key={solutionIndex}
                            className="industry-solution-item"
                          >

                            <CheckCircle2
                              size={14}
                            />

                            <span>
                              {solution}
                            </span>

                          </div>

                        )
                      )}

                    </div>


                    <Link
                      to="/contact"
                      className="industry-explore-link"
                    >

                      Discuss Your Requirements

                      <ArrowUpRight
                        size={16}
                      />

                    </Link>

                  </div>


                  <div className="industry-card-bottom-line"></div>

                </article>

              )
            )}

          </div>

        </div>

      </section>


      {/* =====================================================
          CAPABILITIES
      ===================================================== */}

      <section
        ref={capabilitiesRef}
        className={`industry-capabilities ${
          visibleSections.capabilities
            ? "industry-section-visible"
            : ""
        }`}
      >

        <div className="industry-section-grid"></div>

        <div className="industries-container">


          <div className="industry-heading-row capabilities-heading">

            <div className="industry-reveal-left">

              <span className="industry-section-label">
                WHAT WE DELIVER
              </span>


              <h2>
                Technology that
                <br />
                <span>
                  moves your business.
                </span>
              </h2>

            </div>


            <p className="industry-reveal-right">
              Regardless of your industry, our solutions can combine
              modern software development, AI and automation to create
              a stronger digital foundation for your business.
            </p>

          </div>


          <div className="industry-capabilities-grid">

            {capabilities.map(
              (capability, index) => (

                <div
                  className={`industry-capability-card capability-card-${(
                    index % 3
                  ) + 1}`}
                  key={index}
                >

                  <div className="capability-card-grid"></div>


                  <div className="capability-icon">

                    {capability.icon}

                  </div>


                  <div>

                    <h3>
                      {capability.title}
                    </h3>


                    <p>
                      {capability.text}
                    </p>

                  </div>

                </div>

              )
            )}

          </div>

        </div>

      </section>


      {/* =====================================================
          WHY NEXIS FOLD
      ===================================================== */}

      <section
        ref={whyRef}
        className={`industry-why ${
          visibleSections.why
            ? "industry-section-visible"
            : ""
        }`}
      >

        <div className="industries-container">

          <div className="industry-why-box">

            <div className="industry-why-grid"></div>

            <div className="industry-why-glow"></div>


            <div className="industry-why-content industry-reveal-left">

              <span className="industry-section-label">
                WHY NEXIS FOLD
              </span>


              <h2>
                Technology should
                <br />
                <span>
                  solve the problem.
                </span>
              </h2>


              <p>
                We don't believe in one-size-fits-all software.
                Every project starts by understanding your business,
                your users and the problem you actually need to solve.
              </p>

            </div>


            <div className="industry-why-points">

              <div className="why-point-1">

                <span>
                  01
                </span>


                <div>

                  <strong>
                    Business-first thinking
                  </strong>


                  <p>
                    We start with your goals and requirements,
                    not technology for technology's sake.
                  </p>

                </div>

              </div>


              <div className="why-point-2">

                <span>
                  02
                </span>


                <div>

                  <strong>
                    Scalable architecture
                  </strong>


                  <p>
                    We build systems that can evolve as your
                    users, data and business requirements grow.
                  </p>

                </div>

              </div>


              <div className="why-point-3">

                <span>
                  03
                </span>


                <div>

                  <strong>
                    Modern technology
                  </strong>


                  <p>
                    We use modern development practices,
                    AI and automation where they create real value.
                  </p>

                </div>

              </div>


              <div className="why-point-4">

                <span>
                  04
                </span>


                <div>

                  <strong>
                    Long-term partnership
                  </strong>


                  <p>
                    We focus on creating maintainable products
                    rather than delivering technology and walking away.
                  </p>

                </div>

              </div>

            </div>

          </div>

        </div>

      </section>


      {/* =====================================================
          CTA
      ===================================================== */}

      <section
        ref={ctaRef}
        className={`industry-cta ${
          visibleSections.cta
            ? "industry-section-visible"
            : ""
        }`}
      >

        <div className="industries-container">

          <div className="industry-cta-box">

            <div className="industry-cta-grid"></div>

            <div className="industry-cta-glow"></div>


            <div className="industry-cta-content">

              <span className="industry-section-label">
                HAVE A BUSINESS CHALLENGE?
              </span>


              <h2>
                Let's build the right
                <br />
                <span>
                  solution for your industry.
                </span>
              </h2>


              <p>
                Tell us about your business, workflow or idea.
                We'll help you identify where technology,
                software, AI or automation can create the most value.
              </p>


              <Link
                to="/contact"
                className="industries-primary-btn"
              >
                Start a Conversation

                <ArrowUpRight
                  size={17}
                />

              </Link>

            </div>

          </div>

        </div>

      </section>


    </div>
  );
}

export default Industries;